import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule, isDevMode } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { ClipboardModule } from 'ngx-clipboard';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HttpInterceptorService } from './interceptors/http-interceptor.service';
import { EditcollectionComponent } from './module/manage-collection/editcollection/editcollection.component';
import { Const, initialUserDetails } from './shared/const/const';
import { CopyDirective } from './shared/directives/copy.directive';
import { ErrorHandlerDirective } from './shared/directives/error-handler.directive';
import { ManageTransactionsDirective } from './shared/directives/manage-transactions.directive';
import { CommonHelper } from './shared/helpers/common-helper';
import { SearchPipe } from './shared/pipes/search.pipe';

import { NgIdleModule } from '@ng-idle/core';
import { TrimWhitespacesDirective } from './shared/directives/trim-whitespaces.directive';
import { SharedModule } from './shared/shared.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { SwUpdateNavigationService } from './shared/services/sw-update-navigation.service';
import { SwProcessingInterceptor } from './interceptors/sw-processing.interceptor';
import { ServiceWorkerBypassInterceptor } from './interceptors/service-worker-bypass.interceptor';
import { ImagePreviewComponent } from './shared/component/image-preview/image-preview.component';
import { TelegramErrorInterceptor } from './interceptors/telegram-error.interceptor';
import { TelegramErrorReporterService } from './core/services/telegram-error-reporter.service';
import { TelegramErrorHandler } from './core/services/telegram-error-handler';
import { TrackedToastrService } from './core/services/tracked-toastr.service';

/**
 * Builds the APP_INITIALIZER that attaches the window-level error listeners.
 * @param {TelegramErrorReporterService} reporter the alert reporter
 * @returns {() => void} the initializer
 */
function installErrorListeners(reporter: TelegramErrorReporterService): () => void {
    return () => reporter.installGlobalListeners();
}

/**
 * Builds the APP_INITIALIZER that applies a pending new-build update on the next page navigation.
 * @param {SwUpdateNavigationService} updateNavigation the navigation-time updater
 * @returns {() => void} the initializer
 */
function startUpdateNavigation(updateNavigation: SwUpdateNavigationService): () => void {
    return () => updateNavigation.start();
}


@NgModule({
    declarations: [
        AppComponent,
        EditcollectionComponent,
        SearchPipe,
        CopyDirective,
        ErrorHandlerDirective,
        ManageTransactionsDirective,
        TrimWhitespacesDirective,

    ],
    imports: [
    RouterModule,
    SharedModule,
    CommonModule,
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    ToastrModule.forRoot({
        timeOut: 5000,
        positionClass: 'toast-top-right',
        preventDuplicates: true,
    }),
    BrowserAnimationsModule,
    ClipboardModule,
    NgIdleModule.forRoot(),
    ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: !isDevMode(),
        // Register the ServiceWorker as soon as the application is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: 'registerWhenStable:30000'
    }),
    ImagePreviewComponent
],
    providers: [
        CommonHelper,
        Const,
        initialUserDetails,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpInterceptorService,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SwProcessingInterceptor,
            multi: true
        },
        // Report-only: rethrows every error unchanged after alerting on backend failures.
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TelegramErrorInterceptor,
            multi: true
        },
        // Innermost: tags FormData uploads with ?ngsw-bypass so iOS Safari's service worker cannot drop the file body.
        { provide: HTTP_INTERCEPTORS, useClass: ServiceWorkerBypassInterceptor, multi: true },
        // Uncaught errors keep Angular's default console output and are also alerted.
        { provide: ErrorHandler, useClass: TelegramErrorHandler },
        // Window-level error/rejection listeners, installed before any feature code runs.
        {
            provide: APP_INITIALIZER,
            useFactory: installErrorListeners,
            deps: [TelegramErrorReporterService],
            multi: true
        },
        // A pending new build is applied on the next page navigation (see SwUpdateNavigationService).
        {
            provide: APP_INITIALIZER,
            useFactory: startUpdateNavigation,
            deps: [SwUpdateNavigationService],
            multi: true
        },
        // Every `toastr.error(...)` in the app is mirrored to the alert channel. Overriding
        // the root ToastrService covers all existing call sites without editing any of them.
        { provide: ToastrService, useClass: TrackedToastrService }
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {

}
